import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const ROW_ACTION_ICONS_CLASS = "row-action-icons";

const MENU_CLOSE_DELAY_MS = 120;

/** Vertical ellipsis — kept local to avoid IconLib ↔ RowActionIcons circular import. */
function MoreVertIcon({ size = 18 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

/**
 * Compact row actions for tables/cards.
 * Default `variant="menu"` (table Action column): always-visible ⋮; hover opens icon+label dropdown.
 * Use `variant="inline"` for card footers (always-visible icons, prior UX).
 *
 * Action buttons should keep `title` (used as the menu label via CSS `attr(title)`).
 */
export function RowActionIcons({
  children,
  className = "",
  as: Tag = "span",
  variant = "menu",
  menuAlign = "end",
  "aria-label": ariaLabel = "Row actions",
}) {
  const menuId = useId();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  /** Keep portal mounted after first open so nested modals (e.g. labels) survive menu close. */
  const [menuMounted, setMenuMounted] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth || 180;
    const menuHeight = menuEl?.offsetHeight || 0;
    const gap = 4;
    const viewportPadding = 8;

    let left = menuAlign === "start" ? rect.left : rect.right - menuWidth;
    left = Math.min(left, window.innerWidth - menuWidth - viewportPadding);
    left = Math.max(viewportPadding, left);

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding && rect.top > menuHeight + gap) {
      top = rect.top - menuHeight - gap;
    }
    top = Math.max(viewportPadding, top);

    setMenuPos({ top, left });
  }, [menuAlign]);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setMenuMounted(true);
    setMenuOpen(true);
  }, [clearCloseTimer]);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setMenuOpen(false);
      closeTimerRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!menuOpen) return;
    updateMenuPosition();
  }, [menuOpen, children, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onReposition = () => updateMenuPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  if (variant === "inline") {
    return <Tag className={`${ROW_ACTION_ICONS_CLASS} ${className}`.trim()}>{children}</Tag>;
  }

  const rootClass = `${ROW_ACTION_ICONS_CLASS} ${ROW_ACTION_ICONS_CLASS}--menu ${
    menuOpen ? `${ROW_ACTION_ICONS_CLASS}--menu-open` : ""
  } ${className}`.trim();

  return (
    <Tag className={rootClass}>
      <span
        className={`${ROW_ACTION_ICONS_CLASS}__trigger-wrap`}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleCloseMenu}
      >
        <button
          ref={triggerRef}
          type="button"
          className={`${ROW_ACTION_ICONS_CLASS}__trigger`}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          onFocus={openMenu}
          onClick={(e) => {
            // Touch / keyboard: toggle. Hover already opens on pointer devices.
            e.preventDefault();
            e.stopPropagation();
            clearCloseTimer();
            setMenuMounted(true);
            setMenuOpen((prev) => !prev);
          }}
        >
          <MoreVertIcon size={18} />
        </button>
      </span>
      {typeof document !== "undefined" &&
        menuMounted &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className={`${ROW_ACTION_ICONS_CLASS}__menu${menuOpen ? ` ${ROW_ACTION_ICONS_CLASS}__menu--open` : ""}`}
            style={{ top: menuPos.top, left: menuPos.left }}
            role="menu"
            aria-hidden={!menuOpen}
            onMouseEnter={openMenu}
            onMouseLeave={scheduleCloseMenu}
            onClick={(e) => {
              if (e.target.closest("button, a")) {
                scheduleCloseMenu();
              }
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </Tag>
  );
}
